import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import moment from 'moment';
import { getUserFirstWeekDay } from '@mummssoftware/utils/utils';
import {
  SET_FROM_DATE,
  SET_INCLUDE_REFUSED,
  SET_PATIENT_NUMBER,
  SET_TO_DATE,
} from './constants';
import { PatientAssistanceContext } from '../../context';

type AssistanceContextProviderProps = {
  children: ReactNode;
};

type AssistanceContext = {
  patientNumber: string | null;
  fromDate: Date | null;
  toDate: Date | null;
  includeRefused: boolean;
  dispatch?: any;
};

type AssistanceContextState = {
  patientNumber: string | null;
  fromDate: Date | null;
  toDate: Date | null;
  includeRefused: boolean;
};

const staticInitialState = {
  patientNumber: null,
  toDate: new Date(),
  includeRefused: true,
};

const initAssistanceContext = {
  patientNumber: null,
  fromDate: null,
  toDate: null,
  includeRefused: true,
};

export const AssistanceContextReducer = (
  state: AssistanceContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_PATIENT_NUMBER:
      newState.patientNumber = action.payload;
      break;
    case SET_FROM_DATE:
      newState.fromDate = action.payload;
      break;
    case SET_TO_DATE:
      newState.toDate = action.payload;
      break;
    case SET_INCLUDE_REFUSED:
      newState.includeRefused = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const AssistanceContext = createContext<AssistanceContext>(
  initAssistanceContext as AssistanceContext,
);

const getDateNDaysFromStartOfWeek = (nDays: number, currentUser: mumms.User) =>
  moment(getUserFirstWeekDay(currentUser)).subtract(nDays, 'days').toDate();

export const AssistanceContextProvider = ({
  children,
}: AssistanceContextProviderProps) => {
  const { patient, currentUser } = useContext(PatientAssistanceContext);

  const initialFromDateDistance = patient ? 21 : 7;

  const initialFromData = getDateNDaysFromStartOfWeek(
    initialFromDateDistance,
    currentUser,
  );

  const [
    { patientNumber, fromDate, toDate, includeRefused },
    dispatch,
  ] = useReducer(AssistanceContextReducer, {
    ...staticInitialState,
    fromDate: initialFromData,
  });

  return (
    <AssistanceContext.Provider
      value={{
        patientNumber,
        fromDate,
        toDate,
        includeRefused,
        dispatch,
      }}
    >
      {children}
    </AssistanceContext.Provider>
  );
};

export default AssistanceContextProvider;
